
import {
  CareerSiteLocationResponse,
  CareerSiteService,
} from 'api-clients/monolith';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Global cache shared across all useLocations instances
const globalLocationsCache = new Map<
  string,
  {
    data: CareerSiteLocationResponse['locations'];
    pagination: CareerSiteLocationResponse['pagination'];
    timestamp: number;
  }
>();

/**
 * Test-only affordance to clear the module-level locations cache between tests.
 * Do not use in production code.
 */
export function _clearLocationsCache(): void {
  globalLocationsCache.clear();
}

interface UseLocationsParams {
  accountSlug: string;
  brandId: string;
  query?: string;
  perPage?: number;
}

interface UseLocationsReturn {
  data: CareerSiteLocationResponse['locations'];
  isFetching: boolean;
  hasMore: boolean;
  loadMore: () => void;
  refetch: () => void;
  pagination?: CareerSiteLocationResponse['pagination'];
}

export function useLocations({
  accountSlug,
  brandId,
  query = '',
  perPage = 50,
}: UseLocationsParams): UseLocationsReturn {
  const [data, setData] = useState<CareerSiteLocationResponse['locations']>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] =
    useState<CareerSiteLocationResponse['pagination']>();
  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);

  // Memoize the cache key to prevent unnecessary refetches
  const cacheKey = useMemo(() => {
    return `${accountSlug}-${brandId}-${query}-${perPage}`;
  }, [accountSlug, brandId, query, perPage]);

  const fetchData = useCallback(
    async (pageNum: number, searchQuery: string, reset = false) => {
      // Check global cache first for the first page
      if (pageNum === 1 && reset) {
        const cachedData = globalLocationsCache.get(cacheKey);
        if (cachedData && Date.now() - cachedData.timestamp < 300000) {
          // 5 minutes cache
          setData(cachedData.data);
          setPagination(cachedData.pagination);
          return;
        }
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      abortControllerRef.current = new AbortController();

      setIsFetching(true);

      try {
        const request = CareerSiteService.getInternalApiCareerSiteLocations(
          {
            account_slug: accountSlug,
            brand_id: brandId,
          },
          searchQuery.trim() || undefined,
          pageNum,
          perPage,
        );

        const abortListener = () => request.cancel();
        abortControllerRef.current.signal.addEventListener(
          'abort',
          abortListener,
        );

        const result = (await request) as CareerSiteLocationResponse;

        abortControllerRef.current?.signal.removeEventListener(
          'abort',
          abortListener,
        );

        if (abortControllerRef.current?.signal.aborted) {
          return;
        }

        setPagination(result.pagination);

        if (reset) {
          setData(result.locations);
          // Cache the first page data in global cache
          if (pageNum === 1) {
            globalLocationsCache.set(cacheKey, {
              data: result.locations,
              pagination: result.pagination,
              timestamp: Date.now(),
            });
          }
        } else {
          setData(prev => [...prev, ...result.locations]);
        }
      } catch {
        // fetch error — not re-thrown
      } finally {
        if (isMountedRef.current) {
          setIsFetching(false);
        }
      }
    },
    [accountSlug, brandId, perPage, cacheKey],
  );

  const loadMore = useCallback(() => {
    if (!isFetching && pagination && currentPage < pagination.total_pages) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      void fetchData(nextPage, query, false);
    }
  }, [currentPage, fetchData, isFetching, pagination, query]);

  const refetch = useCallback(() => {
    setCurrentPage(1);
    void fetchData(1, query, true);
  }, [fetchData, query]);

  const hasMore = pagination ? currentPage < pagination.total_pages : false;

  useEffect(() => {
    setCurrentPage(1);
    void fetchData(1, query, true);

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [query, fetchData]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return { data, isFetching, hasMore, loadMore, refetch, pagination };
}
